import { ApiService, type ApiRequestConfig } from "./api.service";
import type { components } from "@/types/api.generated";

export type AutomationSettings = components["schemas"]["AutomationSettings"];
export type AutomationRule = components["schemas"]["AutomationRule"];
export type AutomationRuleEnum = components["schemas"]["AutomationRuleEnum"];
export type AutomationRuleChangeRequest = components["schemas"]["AutomationRuleChangeRequest"];
/** The 400 body: one rule's change was refused, naming the rule and field. */
export type AutomationSettingsError = components["schemas"]["AutomationSettingsError"];
/** A project's Automations tab (NG-3688): its own work-item rules, whether the viewer
 *  may change them, and the team-level rules its home team sets. */
export type ProjectAutomationSettings = components["schemas"]["ProjectAutomationSettings"];
/** One team Apply to teams offers, with its leads (NG-3691). */
export type AutomationApplyTeam = components["schemas"]["AutomationApplyTeam"];
export type AutomationApplyTeams = components["schemas"]["AutomationApplyTeams"];
export type AutomationApplyResponse = components["schemas"]["AutomationApplyResponse"];

/**
 * Whose automation settings a call is about: a workspace slug alone means the
 * workspace defaults (NG-3529); `{ slug, teamId }` means that team's own
 * settings (NG-3681), under `workspaces/<slug>/teams/<teamId>/automation-settings/`.
 * The two share every route below the prefix, so each draft method takes either.
 */
type AutomationSettingsOwner = string | { slug: string; teamId: string };

function settingsUrl(owner: AutomationSettingsOwner): string {
  if (typeof owner === "string") return `/api/workspaces/${owner}/automation-settings/`;
  return `/api/workspaces/${owner.slug}/teams/${owner.teamId}/automation-settings/`;
}

/**
 * Automation settings drafts: the workspace defaults (NG-3529) or a team's
 * (NG-3681), see {@link AutomationSettingsOwner}. Every write sends the version
 * it was based on as `If-Match`, and the server answers 409 with the current
 * draft when that version is stale, so two editors can't overwrite each other.
 */
export class AutomationSettingsService extends ApiService {
  private draftUrl(owner: AutomationSettingsOwner): string {
    return `${settingsUrl(owner)}draft/`;
  }

  private ifMatch(version: string) {
    return { headers: { "If-Match": version } };
  }

  /** The draft (the published settings until someone edits). */
  async getDraft(
    owner: AutomationSettingsOwner,
    config?: ApiRequestConfig,
  ): Promise<AutomationSettings> {
    return this.get(this.draftUrl(owner), config);
  }

  /** Save the changed rules into the draft; nothing goes live. */
  async saveDraft(
    owner: AutomationSettingsOwner,
    rules: AutomationRuleChangeRequest[],
    version: string,
  ): Promise<AutomationSettings> {
    return this.patch(this.draftUrl(owner), { rules }, this.ifMatch(version));
  }

  /** Make the draft live; answers with the published settings. */
  async publish(owner: AutomationSettingsOwner, version: string): Promise<AutomationSettings> {
    return this.post(`${this.draftUrl(owner)}publish/`, undefined, this.ifMatch(version));
  }

  /** Throw the draft away; answers with the published settings. */
  async discard(owner: AutomationSettingsOwner, version: string): Promise<AutomationSettings> {
    return this.post(`${this.draftUrl(owner)}discard/`, undefined, this.ifMatch(version));
  }

  /** What new teams get now: the published defaults, without the draft. */
  async getPublished(slug: string, config?: ApiRequestConfig): Promise<AutomationSettings> {
    return this.get(`/api/workspaces/${slug}/automation-settings/`, config);
  }

  private projectUrl(slug: string, projectId: string): string {
    return `/api/workspaces/${slug}/projects/${projectId}/automation-settings/`;
  }

  /** A project's automations and what its team sets (NG-3688). */
  async getProject(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<ProjectAutomationSettings> {
    return this.get(this.projectUrl(slug, projectId), config);
  }

  /** Save a project's changed rules; they apply at once (a project has no draft). */
  async saveProject(
    slug: string,
    projectId: string,
    rules: AutomationRuleChangeRequest[],
    version: string,
  ): Promise<ProjectAutomationSettings> {
    return this.patch(this.projectUrl(slug, projectId), { rules }, this.ifMatch(version));
  }

  /** Every live team with its leads, for Apply to teams (NG-3691). */
  async listApplyTeams(slug: string, config?: ApiRequestConfig): Promise<AutomationApplyTeams> {
    return this.get(`/api/workspaces/${slug}/automation-settings/apply/teams/`, config);
  }

  /** Replace the teams' published automations with the published defaults. */
  async applyToTeams(slug: string, teamIds: string[]): Promise<AutomationApplyResponse> {
    return this.post(`/api/workspaces/${slug}/automation-settings/apply/`, { team_ids: teamIds });
  }
}
